// Enhanced Gigya JS SDK integration with environment variables support

interface GigyaScreenEvent {
  screenSet: string;
  currentScreen: string;
  errorCode: string;
  [key: string]: any; // Additional contextual data provided by specific events
}

// Gigya SDK interface types
interface GigyaAccounts {
  getJWT: (opts: { callback: (data: unknown) => void }) => void;
  logout?: (params?: { callback?: () => void }) => void;
  showScreenSet?: (opts: { screenSet: string; startScreen: string; containerID: string; lang?: string; onAfterSubmit?: (eventObj: GigyaScreenEvent) => void; }) => void;
}

interface GigyaSSO {
  login: (opts: {
    authFlow: string;
    redirectURL: string;
    context: {
      brand: string;
      locale: string;
      action: string;
    };
  }) => void;
}

interface GigyaSDK {
  accounts: GigyaAccounts;
  sso?: GigyaSSO;
}

export interface WindowWithGigya extends Window {
  gigya?: GigyaSDK;
  gigya_conf?: GigyaConf;
  fsl_locale?: string;
}

interface GigyaConf {
  apiDomain: string;
}

// ==== NEW CACHE IMPLEMENTATION ====
// Cache for token and expiration time
let tokenCache: { token: string; expiresAt: number } | null = null;
let pendingTokenRequest: Promise<string> | null = null;

// Helper function to decode JWT expiration
function getTokenExpiration(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp; // Expiration time in seconds
  } catch (error) {
    console.error('[Gigya Service] Error decoding token expiration:', error);
    return Date.now() / 1000 + 300; // Default to 5 minutes if decoding fails
  }
}

// Centralized token fetching with caching and concurrency control
async function fetchTokenWithCache(): Promise<string> {
  // Return cached token if valid
  if (tokenCache && tokenCache.expiresAt > Date.now() / 1000) {
    console.debug('[Gigya Service] Using cached token');
    return tokenCache.token;
  }

  // Return pending request if exists
  if (pendingTokenRequest) {
    console.debug('[Gigya Service] Returning pending token request');
    return pendingTokenRequest;
  }

  // Create new request
  pendingTokenRequest = new Promise((resolve, reject) => {
    (async () => {
      try {
        console.debug('[Gigya Service] Fetching new token from Gigya');
        const gigyaInstance = await waitForGigya();
        if (!gigyaInstance) {
          throw new Error('Gigya SDK not loaded');
        }

        const token = await new Promise<string>((innerResolve, innerReject) => {
          gigyaInstance.accounts.getJWT({
            callback: (data: unknown) => {
              if (
                typeof data === 'object' &&
                data !== null &&
                'id_token' in data &&
                typeof (data as { id_token: unknown }).id_token === 'string'
              ) {
                const token = (data as { id_token: string }).id_token;
                const expiresAt = getTokenExpiration(token);
                tokenCache = { token, expiresAt };
                console.debug('[Gigya Service] New token received and cached');
                innerResolve(token);
              } else {
                innerReject(new Error('No id_token from Gigya'));
              }
            },
          });
        });

        // Clear pending request and resolve
        pendingTokenRequest = null;
        resolve(token);
      } catch (error) {
        // Clear pending request on error
        pendingTokenRequest = null;
        reject(error);
      }
    })();
  });

  return pendingTokenRequest;
}

// ==== UPDATE EXISTING FUNCTIONS ====
export async function getGigyaToken(): Promise<string> {
  console.debug('[Gigya Service] getGigyaToken - START');

  if (import.meta.env.VITE_USE_MOCKOON === 'true') {
    console.debug('[Gigya Service] getGigyaToken - Using mock Gigya token');
    return 'mock-gigya-token';
  }

  // Use cached token if available
  return fetchTokenWithCache();
}

// Wait for Gigya SDK to be available with enhanced diagnostics
async function waitForGigya(): Promise<GigyaSDK | null> {
  console.debug('[Gigya Service] waitForGigya - START - Checking for window.gigya...');

  // Check network connectivity to Gigya first
  const dataCenter = import.meta.env.VITE_GIGYA_DATA_CENTER ?? 'us1';
  const apiKey = import.meta.env.VITE_GIGYA_API_KEY;

  console.debug(
    `[Gigya Service] waitForGigya - Data Center: ${dataCenter}, API Key: ${apiKey ? 'present' : 'missing'}`
  );

  return new Promise((resolve) => {
    let attempts = 0;
    const maxAttempts = 150; // Increased to 15 seconds with 100ms intervals

    const checkGigya = () => {
      attempts++;
      const win = window as WindowWithGigya;

      // Log progress every 2 seconds instead of every attempt
      if (attempts % 20 === 0 || attempts === 1) {
        console.debug(
          `[Gigya Service] waitForGigya - Attempt ${attempts}/${maxAttempts} (${((attempts * 100) / 1000).toFixed(1)}s)`
        );
        console.debug('[Gigya Service] waitForGigya - window.gigya exists:', !!win.gigya);

        // Check if Gigya script is in DOM
        const gigyaScript = document.querySelector(`script[src*="gigya.js"]`);
        console.debug('[Gigya Service] waitForGigya - Gigya script in DOM:', !!gigyaScript);

        if (gigyaScript) {
          const scriptSrc = gigyaScript.getAttribute('src');
          console.debug('[Gigya Service] waitForGigya - Script source:', scriptSrc);
        }
      }

      if (win.gigya) {
        console.debug(
          '[Gigya Service] waitForGigya - window.gigya.accounts exists:',
          !!win.gigya.accounts
        );
        if (win.gigya.accounts) {
          console.debug(
            '[Gigya Service] waitForGigya - getJWT function type:',
            typeof win.gigya.accounts.getJWT
          );
        }
      }

      if (typeof win.gigya?.accounts?.getJWT === 'function') {
        console.debug(
          `[Gigya Service] waitForGigya - SUCCESS - Gigya SDK is available after ${((attempts * 100) / 1000).toFixed(1)}s`
        );
        resolve(win.gigya);
      } else if (attempts >= maxAttempts) {
        console.warn(
          `[Gigya Service] waitForGigya - TIMEOUT - Waiting for Gigya SDK after ${(maxAttempts * 100) / 1000}s`
        );
        console.warn('[Gigya Service] waitForGigya - Final state check:', {
          gigyaExists: !!win.gigya,
          accountsExists: !!win.gigya?.accounts,
          getJWTExists: !!win.gigya?.accounts?.getJWT,
          userAgent: navigator.userAgent,
          connectionType:
            (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
              ?.effectiveType ?? 'unknown',
        });
        resolve(null);
      } else {
        setTimeout(checkGigya, 100);
      }
    };
    checkGigya();
  });
}

// Check if user is logged in to Gigya by attempting to get a JWT
export async function isGigyaLoggedIn(): Promise<boolean> {
  console.log('🔐 [Gigya Service] isGigyaLoggedIn - START');

  if (import.meta.env.VITE_USE_MOCKOON === 'true') {
    console.log('🎭 [Gigya Service] isGigyaLoggedIn - Using mock mode, returning true');
    return true;
  }

  // Make timeouts configurable via environment variables
  const maxRetries = parseInt(import.meta.env.VITE_GIGYA_MAX_RETRIES ?? '3', 10);
  const baseTimeout = parseInt(import.meta.env.VITE_GIGYA_TIMEOUT_MS ?? '5000', 10);

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔐 [Gigya Service] isGigyaLoggedIn - Attempt ${attempt}/${maxRetries}`);

      // First check if Gigya SDK is available
      const gigyaInstance = await waitForGigya();
      if (!gigyaInstance) {
        console.warn('🔐 [Gigya Service] isGigyaLoggedIn - Gigya SDK not available');
        return false;
      }

      // Progressive timeout: 5s, 10s, 15s
      const timeoutDuration = baseTimeout * attempt;
      console.log(`🔐 [Gigya Service] isGigyaLoggedIn - Using timeout: ${timeoutDuration}ms`);

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error(`Gigya login check timeout (${timeoutDuration}ms)`)),
          timeoutDuration
        )
      );

      await Promise.race([fetchTokenWithCache(), timeoutPromise]);
      console.log('🔐 [Gigya Service] isGigyaLoggedIn - SUCCESS: User is logged in');
      return true;
    } catch (error) {
      const isLastAttempt = attempt === maxRetries;
      console.warn(
        `🔐 [Gigya Service] isGigyaLoggedIn - Attempt ${attempt} FAILED:`,
        error instanceof Error ? error.message : error
      );

      if (isLastAttempt) {
        console.warn(
          '🔐 [Gigya Service] isGigyaLoggedIn - ALL ATTEMPTS FAILED: User not logged in or persistent error occurred:',
          error
        );
        return false;
      }

      // Wait before retry (exponential backoff: 1s, 2s)
      const waitTime = 1000 * attempt;
      console.log(`🔐 [Gigya Service] isGigyaLoggedIn - Waiting ${waitTime}ms before retry...`);
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
  }

  return false;
}

// Perform Gigya logout with callback
export async function gigyaLogout(): Promise<void> {
  console.log('🚪 [Gigya Service] gigyaLogout - START');

  const win = window as WindowWithGigya;

  if (!win.gigya?.accounts?.logout) {
    console.warn('🚪 [Gigya Service] gigyaLogout - Logout method not available');
    return;
  }

  console.log('🚪 [Gigya Service] Clearing Gigya session...');

  // Wait for Gigya logout to complete before proceeding
  await new Promise<void>((resolve) => {
    const timeoutId = setTimeout(() => {
      console.warn('🚪 [Gigya Service] Gigya logout timeout, proceeding anyway');
      resolve();
    }, 2000); // 2 second timeout as fallback

    win.gigya!.accounts.logout!({
      callback: () => {
        clearTimeout(timeoutId);
        console.log('🚪 [Gigya Service] Gigya session cleared successfully');
        resolve();
      },
    });
  });
}

// Perform SSO login with redirect back to current page (without query params)
export async function gigyaSSOLogin(redirectUrl?: string): Promise<void> {
  console.log('🔐 [Gigya Service] gigyaSSOLogin - START');

  const { getFoodShelterLoveRedirectUrl } = await import('../utils/redirectUrl');
  const redirectURL = redirectUrl ?? getFoodShelterLoveRedirectUrl();

  if (import.meta.env.VITE_USE_MOCKOON === 'true') {
    console.log('🎭 [Gigya Service] gigyaSSOLogin - Mock mode, redirecting to current URL');
    window.location.href = redirectURL;
    return;
  }

  try {
    const gigyaInstance = await waitForGigya();
    if (!gigyaInstance) {
      console.warn('🔐 [Gigya Service] gigyaSSOLogin - Gigya SDK unavailable, fallback redirect');
      window.location.href = redirectURL;
      return;
    }

    if (gigyaInstance.sso && typeof gigyaInstance.sso.login === 'function') {
      console.log('🔐 [Gigya Service] gigyaSSOLogin - Calling gigya.sso.login');
      const locale = ((window as WindowWithGigya).fsl_locale ?? 'en-us')
        .toLowerCase()
        .replace('-', '_');
      gigyaInstance.sso.login({
        authFlow: 'redirect',
        redirectURL,
        context: {
          brand: 'hillsvet',
          locale,
          action: 'login',
        },
      });
      return;
    }

    console.warn('🔐 [Gigya Service] gigyaSSOLogin - SSO API not available, fallback redirect');
    window.location.href = redirectURL;
  } catch (error) {
    console.error('❌ [Gigya Service] gigyaSSOLogin - Error during SSO login:', error);
    window.location.href = redirectURL;
  }
}

export async function gigyaLogoutAndRegister(redirectURL: string): Promise<void> {
  await gigyaLogout();
  try {
    const gigyaInstance = await waitForGigya();
    if (!gigyaInstance) {
      console.warn('🔐 [Gigya Service] gigyaSSOLogin - Gigya SDK unavailable, fallback redirect');
      window.location.href = redirectURL;
      return;
    }

    if (gigyaInstance.sso && typeof gigyaInstance.sso.login === 'function') {
      console.log('🔐 [Gigya Service] gigyaSSOLogin - Calling gigya.sso.login');
      const locale = ((window as WindowWithGigya).fsl_locale ?? 'en-us')
        .toLowerCase()
        .replace('-', '_');
      gigyaInstance.sso.login({
        authFlow: 'redirect',
        redirectURL,
        context: {
          brand: 'hillsvet',
          locale,
          action: 'register',
        },
      });
      return;
    }

    console.warn('🔐 [Gigya Service] gigyaSSOLogin - SSO API not available, fallback redirect');
    window.location.href = redirectURL;
  } catch (error) {
    console.error('❌ [Gigya Service] gigyaSSOLogin - Error during SSO login:', error);
    window.location.href = redirectURL;
  }
}

export async function gigyaLogoutAndLogin(redirectUrl?: string): Promise<void> {
  await gigyaLogout();
  await gigyaSSOLogin(redirectUrl);
}
export async function gigyaLogoutAndRedirect(redirectUrl?: string): Promise<void> {
  const { getFoodShelterLoveRedirectUrl } = await import('../utils/redirectUrl');
  const redirect = redirectUrl ?? getFoodShelterLoveRedirectUrl();
  await gigyaLogout();
  window.location.href = redirect;
}

// Initialize Gigya SDK if not already loaded
export function initializeGigya(): void {
  console.log('🚀 [Gigya Service] initializeGigya - START');
  // Only load if not already present and we have the required config
  const apiKey = import.meta.env.VITE_GIGYA_API_KEY;
  const dataCenter = import.meta.env.VITE_GIGYA_DATA_CENTER ?? 'us1';
  const useMockoon = import.meta.env.VITE_USE_MOCKOON;

  console.log('📋 [Gigya Service] initializeGigya - Environment variables:');
  console.log(
    '🔑 [Gigya Service] initializeGigya - VITE_GIGYA_API_KEY:',
    apiKey ? `${apiKey.substring(0, 10)}...` : 'undefined'
  );
  console.log('🌍 [Gigya Service] initializeGigya - VITE_GIGYA_DATA_CENTER:', dataCenter);
  console.log('🔄 [Gigya Service] initializeGigya - VITE_USE_MOCKOON:', useMockoon);

  if (!apiKey) {
    console.warn(
      '⚠️ [Gigya Service] initializeGigya - VITE_GIGYA_API_KEY not configured. Gigya SDK will not be initialized.'
    );
    return;
  }

  const win = window as WindowWithGigya & { gigyaConf?: GigyaConf };

  console.log('🪟 [Gigya Service] initializeGigya - Current window.gigya status:', !!win.gigya);

  // Check if Gigya script already exists in DOM to prevent double loading
  const existingScript = document.querySelector(`script[src*="gigya.js"][src*="apikey=${apiKey}"]`);
  console.log(
    '🔍 [Gigya Service] initializeGigya - Existing Gigya script found:',
    !!existingScript
  );

  if (!win.gigya && !existingScript) {
    console.log(
      '🔄 [Gigya Service] initializeGigya - Gigya SDK not found. Setting up config and injecting script...'
    );

    const apiDomain = `accounts.${dataCenter}.gigya.com`;

    // Dynamically load the Gigya SDK
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://cdns.${dataCenter}.gigya.com/js/gigya.js?apikey=${apiKey}&lang=en&domain=${apiDomain}`;

    script.onload = () => {
      console.log('✅ [Gigya Service] initializeGigya - Gigya SDK script loaded successfully');
      console.log(
        '🪟 [Gigya Service] initializeGigya - window.gigya after load:',
        !!(window as WindowWithGigya).gigya
      );
    };

    script.onerror = (error) => {
      console.error('❌ [Gigya Service] initializeGigya - Failed to load Gigya SDK script:', error);
    };

    document.head.appendChild(script);
    console.log(
      '📄 [Gigya Service] initializeGigya - Gigya SDK script injected into document head'
    );
  } else if (win.gigya) {
    console.log('♻️ [Gigya Service] initializeGigya - Gigya SDK already present');
    console.log(
      '🔍 [Gigya Service] initializeGigya - window.gigya.accounts exists:',
      !!win.gigya?.accounts
    );
  } else if (existingScript) {
    console.log(
      '⚠️ [Gigya Service] initializeGigya - Gigya script already in DOM, waiting for load'
    );
  }
  console.log('✅ [Gigya Service] initializeGigya - END');
}
